import { Component, OnInit, AfterContentInit, ViewChild, OnDestroy, ElementRef, AfterViewInit } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { HttpClient, JsonpInterceptor } from '@angular/common/http';
import { PanelModule } from 'primeng/panel';
import { TableModule } from 'primeng/table';
import { SelectModule } from 'primeng/select';
import { MenuItem } from 'primeng/api';
import { HighchartsChartModule } from 'highcharts-angular';
import { getSymbolIterator } from 'rxjs/internal/symbol/iterator';
import { Observable, BehaviorSubject, of, forkJoin } from 'rxjs';
import * as Highcharts from 'highcharts';
import Annotations from 'highcharts/modules/annotations';
import BrokenAxis from 'highcharts/modules/broken-axis';
import HC_exporting from 'highcharts/modules/exporting';
import HC_exportdata from 'highcharts/modules/export-data';
import { AppConfigService } from './app-config.service';
import { ConfigService } from './config.service';
import { DataLayerService } from './data-layer.service';
import { StationDetailsService } from './station-details.service';
import { ChartUtilitiesService } from './chart-utilities.service';
import { DataWebServicesService } from './data-web-services.service';
import { catchError } from 'rxjs/operators';
Annotations(Highcharts);
BrokenAxis(Highcharts);
HC_exporting(Highcharts);
HC_exportdata(Highcharts);

// this allows access to jquery references such as $("body")
declare var $;
declare var require: any;

// Because typscript must have definitions for all functions and highlight is a custom function:
declare module 'highcharts' {
	let doc;
	let win;
	interface Point {
		highlight: (event: Highcharts.PointerEventObject) => void;
	}
	interface Chart {
		addButton: (options: any) => void;
		contextMenu: (className, items, x, y, width, height, button, event) => void;
	}
}
// CAT - check out https://stackoverflow.com/questions/12454858/display-larger-version-of-image-when-image-clicked/


@Component({
	selector: 'station-dashboard-component',
	standalone: false,
	templateUrl: 'station-dashboard.component.html',
	styleUrls: ['station-dashboard.component.css'],
	providers: [StationDetailsService]
})
export class StationDashboardComponent implements OnInit, AfterViewInit, OnDestroy {
	Highcharts: typeof Highcharts = Highcharts;
	dataLayerService: DataLayerService;
	subscriptions: any[] = [];		// list of subscriptions so they can be ended on ngDestroy

	stationID;						// string value of currently selected station
	listSelection;  				// the numerical value of stationID
	stationList: any[] = [];		// list of all stations

	stationNovaScores: any[] = [];
	alarmTriggers: any[] = [];
	alarmList: any[] = [];			// list of all alarms associted with the selected station to display in a panel

	stationAlarms: any[] = [];		// alarms associated with currently selected station

	allImages: any[] = [];		// list of all images
	images: any[] = [];			// list of images associated with the current station

	elevationProfile: any = null;	// elevation profile definition of currently selected station	
	allElevationProfiles: any[] = [];	// elevation profile dictionary, stationId/source combinations

	currentStation = { stationName: '', stationElevation: '' };
	currentStationSummary;
	tzName = '';
	displayXAxisOnEveryGraph = false;
	dataAccessOptions: MenuItem[];
	startDate;
	endDate;
	currentTab = "data";

	trackerOptions: any[];
	tooltipPreference: string;

	periodOfRecordList = [
		{ label: '3 hours', value: '.125' },
		{ label: '6 hours', value: '.25' },
		{ label: '12 hours', value: '.50' },
		{ label: '1 day', value: '1' },
		{ label: '1 week', value: '7' },
		{ label: '1 month', value: '30' },
		{ label: '3 months', value: '90' },
		{ label: '6 months', value: '180' },
		{ label: '1 year', value: '365' },
		{ label: 'custom', value: '0'}];

	periodOfRecordSelected = '7';

	// This is the list of colors used when more than 1 time series is available for a given data type
	colorList = ['green', 'orange', 'deeppink', 'indigo', 'darkcyan', 'blue'];

	// stationTSArray defines the graph content as well as issues concerning novascore, including
	// the pulldown station list,
	stationTSArray: any[] = [];
	showTools: boolean = false;
	forecastPlotConfiguration: any = null;

	constructor(
		private appConfigService: AppConfigService,
		private configService: ConfigService,
		dataLayerService: DataLayerService,
		private stationDetailsService: StationDetailsService,
		private chartUtilitiesService: ChartUtilitiesService,
		private http: HttpClient,
		private dataWebServices: DataWebServicesService,
		private location: Location,
		private _router: Router,
		private route: ActivatedRoute ) {
		this.dataLayerService = dataLayerService;

		// new
		// Annotations(Highcharts);
		/*
		** In order to have station pulldown automatically selected to the station the user selected to get here
		** (either an actual station if through the map or -999 if through the menu), this code has to occur in the
		** constructor rather than ngOnInit.
		*/
		this.route.params.forEach((params: Params) => {
			this.stationID = +params['id'];  // this stationID is an integer
		});
		this.readAllImages();
		this.readAllElevationProfiles();
		this.stationTSArray = this.appConfigService.getStandardDashboardConfiguration();
		this.displayXAxisOnEveryGraph = this.appConfigService.getDashboardPreference('displayXAxisOnEveryGraph', this.displayXAxisOnEveryGraph );
		this.setStartAndEndDateDefaults();
		// fill the pulldown with the id and name of the available stations
		this.populateStationSelection();
		this.trackerOptions = this.appConfigService.getChartTrackerOptions();
		this.tooltipPreference = this.appConfigService.getChartTooltipPreference();
	}

	ngOnInit() {

		this.dataAccessOptions = [
			{ label: '', icon: 'pi pi-fw pi-chart-bar',
				items: [
					{ label: 'Download CSV', icon: 'pi pi-download', 
						command: (event) => { this.stationDetailsService.downloadData('csv', this.stationID, this.stationTSArray, this.periodOfRecordSelected, this.startDate, this.endDate ); }},
					{ label: 'Download JSON', icon: 'pi pi-download', 
						command: (event) => { this.stationDetailsService.downloadData('json', this.stationID, this.stationTSArray, this.periodOfRecordSelected, this.startDate, this.endDate ); }}
				]
			}
		];

		// this allows the x-axis and tooltip to display the date in the local browser time zone
		Highcharts.setOptions({
			time: {
				useUTC: false
			},
			lang: {
				thousandsSep: ","
			}
		});

		// set the local time zone string
		const d = new Date();
		this.tzName = d.toLocaleString('en', {timeZoneName: 'short'}).split(' ').pop() || "";
		// console.log(this.tzName);

		// this.chartUtilitiesService.bindMouseEvents($('#container'));
		// this.chartUtilitiesService.bindMouseEvents(document.getElementById('StationDashboardHighChartsContainer'));
		this.chartUtilitiesService.fixExportMenu();
	}

	ngAfterViewInit() {
		setTimeout(() => {
			this.chartUtilitiesService.bindMouseEvents( 'StationDashboardHighChartsContainer');
			this.displayGraphsForStationID();
			}, 2000 );
	}

	ngOnDestroy() {
		console.log ("In ngOnDestroy");
		for ( let i = this.subscriptions.length-1; i >= 0;  i--) {
			this.subscriptions[i].unsubscribe();
		}

	}

	setStartAndEndDateDefaults() {
		let today = new Date(Date.now());
		this.endDate = new Date(Date.now());
		this.startDate = new Date (<any>today - (7 * 24 * 60 * 60 * 1000));
	}

	// Displays graphs for a given station id.
	displayGraphsForStationID () {
		console.log("displayGraphsForStationID", this.stationID);
		$('#StationDashboardHighChartsContainer').css('cursor', 'wait' );
		// -999 is used when there is no stationID yet - no graphs to display
		if ( this.stationID === -999 ) {
			return;
		}

		this.displayStationInformation ();
		this.displayImages();
		this.displayElevationProfile();

		const filename = 'stationSummaries_' + this.stationID + '.json';
		const requestObject = {};

		if ( this.periodOfRecordSelected != '0' ) {
			// a duration has been selected
			requestObject['filename'] = filename; 

			// for now, we have to specify a future date to data web services to get future data
			// requestObject['duration'] = this.periodOfRecordSelected;
			const today = new Date(Date.now());
			console.log(today);
			const startDate = new Date(today.getTime() - (+this.periodOfRecordSelected * 24 * 60 * 60 * 1000 ));
			const endDate = new Date(today.getTime() + (24 * 60 * 60 * 1000 )); // 24 hours from now
			requestObject['periodStart'] = startDate;
			requestObject['periodEnd'] = endDate;
		} else {
			// a custom set of dates have been specified by the user
			requestObject['filename'] = filename;
			requestObject['periodStart'] = this.startDate;
			requestObject['periodEnd'] = this.endDate;
		}

		this.stationDetailsService.getStationDetails(requestObject)
			.subscribe (
				tsfile => {
					// console.log(tsfile);
					// console.log(this.stationTSArray);

					// first get all the ts data
					let lastGraphFound = false;

					// Loop through each chart, starting with the last
					for ( let i = this.stationTSArray.length - 1; i >= 0; i-- ) {
						// Loop through each time series per each chart
						for ( let j = 0; j < this.stationTSArray[i].data.length; j++ ) {
							const currentTSElement = this.stationTSArray[i].data[j];
							// console.log(this.stationTSArray[i]);
							// console.log(tsfile.stationSummaries[0]);

							// tsfile.stationSummaries[0] always works because there is only 1 station in each ts file
							currentTSElement.ts = tsfile.stationSummaries[0].ts.filter( currentTS => currentTS.dataType === currentTSElement.dataType);
							currentTSElement.showXAxisLabels = false;

							// If this is wind speed, grab wind direction now as well
							if ( currentTSElement.dataType === 'WindSpeed') {
								currentTSElement['WindDir'] = tsfile.stationSummaries[0].ts.filter(
									currentTS => currentTS.dataType === 'WindDir');
								// console.log(this.stationTSArray[i]);
							}

							// figure out which graph is the last one to have data.  This is the one that will show the x-axis
							if ( currentTSElement.ts != null && currentTSElement.ts.length > 0 && !lastGraphFound ) {
								currentTSElement.showXAxisLabels = true;
								lastGraphFound = true;
								console.log('Last graph is ' + i);
							}
						}
					}

					// data has been gathered, now build the graphs
					this.displayStationTimeSeries ( tsfile );
					console.log(tsfile?.stationSummaries[0]);
					this.displayForecastPlot(tsfile?.stationSummaries[0].typeId);

				},
				error => console.log (error)
			);
		$('#StationDashboardHighChartsContainer').css('cursor', 'default' );
	}


	getAlarmName ( alarm ) {
		let alarmName = '';
		const alarmsArray = this.alarmTriggers.filter(function(stat) {
						return stat.id === alarm.triggerId; });
		if ( alarmsArray.length > 0 ) {
			alarmName = alarmsArray[0].name;
		}
		return alarmName;

	}

	// Takes a string and the max desired length.
	// Returns the same string with newlines, controlling the length of the string.
	breakStringUp ( str, len ) {
		if ( str == null || str === undefined || str.length === 0) {
			return '';
		}
		const regex = new RegExp('[\\s\\S]{1,' + len + '}', 'g');
		const strArray = str.match(regex);
		let newString = '<br>';
		strArray.forEach(element => {
			newString = newString + element + '<br>';
		});
		return newString;
	}

	// Create a message associated with the indicated alarm index to describe the alarm.
	createAlarmMessage ( alarm ) {
		let alarmMessage = '';
		try {
			// console.log(alarm);
			// create the alarm hover message
			alarmMessage += alarm.alarmDateTime +
				'<br>' + alarm.alarmName +
				'<br>Trigger value: ' + alarm.triggerValue + ' ' + alarm.triggerValueUnits +
				this.breakStringUp ( alarm.triggerMessage, 40 );
		} catch (err) {
			console.log('Error processing alarm.');
		}
		return alarmMessage;
	}

	displayStationTimeSeries ( tsfile: any ) {
		// console.log(tsfile);
		console.log("In displayStationTimeSeries");
		const self = this;
		this.alarmList = [];
		this.chartUtilitiesService.clearCharts();

		const today = new Date();
		const exportMenu = this.chartUtilitiesService.getExportMenuDefinition(true);

		const minDate = this.chartUtilitiesService.getMinDate(this.stationTSArray);
		// temporary solution to Precip-Total containing trailing nulls
		this.chartUtilitiesService.removeTrailingNulls(this.stationTSArray );
		let maxDate = this.chartUtilitiesService.getMaxDate(this.stationTSArray); 

		const numberOfDays = this.numberOfDaysInChart ();
		// now massage maxDate based on numberOfDays displayed
		if ( this.periodOfRecordSelected != '0' && maxDate != null ) {
			maxDate = ( maxDate < today.valueOf()) ?  
				today.valueOf() + this.getTodayAdjustment (numberOfDays) : maxDate;
		}
		console.log(minDate);
		console.log(maxDate);

		console.log(numberOfDays);
		const xAxisFormat = 
				this.chartUtilitiesService.formulateXAxisFormat(numberOfDays);
		const tickInterval = 
				this.chartUtilitiesService.calculateTickInterval (numberOfDays);

		// get the time series
		for ( let i = 0; i < this.stationTSArray.length; i++ ) {
			const currentChart = this.stationTSArray[i];
			/*
			if ( currentChart.data[0].ts.length > 0 ) {
				console.log(currentChart);
			}
			*/

			const seriesArray: any[] = [];
			let annotationArray: any[] = [];
			const alarmArray: any[] = [];

			// create the yAxisLabel, which is also the legend
			// show the data type and then data interval (unless it includes the word irreg)
			// const yAxisLabel = dataType + (( ts.dataInterval.toUpperCase().includes('IRREG')) ? ''  : ('.' + ts.dataInterval));
			const showXAxisLabels = currentChart.showXAxisLabels || this.displayXAxisOnEveryGraph;
			const lineColor = ( showXAxisLabels ) ? '#ccd6eb' : 'transparent';
			const tickLength = ( showXAxisLabels ) ? 10 : 0;
			let yAxisLabel = '';
			let yAxis: any[] = [];
			let precision;
			let gapSize;

			for ( let j = 0; j < currentChart.data.length; j++ ) {
				const currentTSElement = currentChart.data[j];

				const tsArray = currentTSElement.ts;
				if ( tsArray != null && tsArray.length > 0 ) {
					for ( let k = 0; k < tsArray.length; k++ ) {
						const dataType = currentTSElement.dataType;
						const isPrecip = currentTSElement.dataType.toUpperCase().includes('PRECIP') ? true : false;  // precip should be displayed from the top of the graph
						const ts = tsArray[k];
						const color = tsArray.length === 1 ? currentTSElement.color : this.colorList[k % this.colorList.length];
						const chartType = currentTSElement.chartType;
						const units = ts.units;
						let seriesLabel = '';  // this is the small legend in the upper right corner of the chart
						const valueDigits = ts.hasOwnProperty('valueDigits') ? ts.valueDigits : null;
						precision = valueDigits != null ? valueDigits : 1;
						gapSize = ts.hasOwnProperty('properties') && ts.properties.hasOwnProperty('pointNoReportInterval') ? ts.properties.pointNoReportInterval*1000 : 86400000;  // # milliseconds in a day

						let currentChartsWithData = 0;
						// create the yAxisLabel, which is also the legend
						// use the label, if specified, from the configuration table, otherwise
						// show the data type and then data interval (unless it includes the word irreg)
						const seriesIdentifierString = currentTSElement.hasOwnProperty('seriesIdentifier') ? currentTSElement.seriesIdentifier : null;
						const seriesIdentifier = ( seriesIdentifierString != null &&  ts.hasOwnProperty(seriesIdentifierString) && ts[seriesIdentifierString] != "") ? 
								ts[seriesIdentifierString] : '';

						let tsname = ts.hasOwnProperty('properties') ? ts.properties.pointTagName : '';
						tsname = tsname != null && tsname.length > 0 ? '(' + tsname + ')' : tsname;

						seriesLabel = 
								( seriesIdentifier != '' ? seriesIdentifier : dataType + tsname ) +
								(( ts.dataInterval.toUpperCase().includes('IRREG')) ? ''  : ('.' + ts.dataInterval));
						// console.log(currentTSElement, seriesIdentifierString, seriesIdentifier );

						// YAxis Label
						// don't display units if they don't exist
						const unitsString = units == "" ? "" : " (" + units + ")";
						const partialLabel = dataType + (( ts.dataInterval.toUpperCase().includes('IRREG')) ? ''  : ('.' + ts.dataInterval)) + unitsString;
						yAxisLabel = currentTSElement.hasOwnProperty('yAxisLabel') ? currentTSElement.yAxisLabel : partialLabel;

						// line/bar vs markers only
						let lineWidth = 2;
						let markerEnabled = false;
						let markerSymbol = currentTSElement.hasOwnProperty('symbol') ? currentTSElement.symbol : 'triangle';
						if ( chartType.toUpperCase()==='SCATTER' || (currentTSElement.hasOwnProperty('markerOnly') && currentTSElement.markerOnly === true) ) {
							console.log('marker only');
							lineWidth = 0;
							markerEnabled = true;
							gapSize = 0; // disregard gapSize - show everything if marker only
						}

						const standardYAxis = {
							title: {
								text: yAxisLabel
							},
							reversed: isPrecip,
							labels: {
								formatter: function () {
									// this allows the y axis labels to be replace with strings rather than their numeric value
									if ( currentTSElement.hasOwnProperty('yAxisReplace')) {
										return self.replaceValue ( this.value, currentTSElement.yAxisReplace );
									} else {  // just return the numerical value
										return this.value.toFixed(precision);
									}
								}
							},
							lineWidth: 3
						}
						// don't keep repeating the same yaxis label over and over for the same data type
						if ( k > 0) {
							standardYAxis.title['enabled'] = false;
							standardYAxis['lineWidth'] = 0;
						}
						
						// merge the standard y-axis with any specifics from the configuration file
						yAxis.push ({...standardYAxis, ...currentTSElement.yAxis });

						// data
						/* This was acceptable when specifying only x and y but not adequate now that sybol is also specified.
						const tsData = ts.data.map(function(obj) {
							const newArray = [];
							//  no processing of time zones necessary because Highcharts.setOptions{global-useUTC} handles internally

							const localDate = new Date(Date.parse(obj.dt));
							newArray.push(localDate.valueOf());

							// value (y axis)
							newArray.push(obj.v);
							// console.log(newArray);
							return newArray;
						});*/

						// data
						const tsData = [];
						this.chartUtilitiesService.convertTSToHighchartsFormat ( tsData, currentTSElement, k, markerEnabled );

						seriesArray.push (
						{
							data: tsData,
							yAxis: currentChartsWithData,
							name: seriesLabel,  // displays data type in the legend
							type: chartType, // defaults to line but we could use "area", etc.
							color: color,
							fillOpacity: 0.3,
							lineWidth: lineWidth,
							marker: {
								enabled: markerEnabled,
								symbol: markerSymbol  // symbol doesn't do anything if enabled is false
							},
							tooltip: {  
								// elements can be accessed during chart formation below through
								// series.tooltipOptions.<element>
								valueDecimals: precision,
								tooltipReplace: currentTSElement.hasOwnProperty('tooltipReplace') ? 
									currentTSElement.tooltipReplace : null
							},
							pointRange: .5 * 3600 * 1000,
							precision: precision
							// sets column width to 30 minute data ... otherwise defaults to smallest time (5 minute data because of stage)
						});

						this.chartUtilitiesService.addAlarmTriggerToSeriesArray( seriesArray, annotationArray, tsfile, minDate, maxDate, dataType, 'upper');

						console.log(this.currentStationSummary);
						if ( this.currentStationSummary != null ) {
							// alarms
							console.log(this.currentStationSummary.alarms);
							for ( let a = 0; a < this.currentStationSummary.alarms.length; a++ ) {
								const alarm = this.currentStationSummary.alarms[a];
								if ( this.doesAlarmMatchDataType(alarm, dataType) ) {
									// If the alarm date/time is within the POR, show the alarm at the proper time and y value.
									// Otherwise, show it at the min date and at the top of the chart.
									const alarmDateTime = new Date(alarm.alarmDateTime);
									const nearestYValue = this.getNearestValueToDate (dataType, alarm.alarmDateTime);
									const triggerValue = alarm.triggerValue + ' ' + alarm.triggerValueUnits;
									alarm.alarmName = this.getAlarmName( alarm );
									const xInPOR = this.isDateTimeWithinPOR (alarmDateTime, minDate, maxDate);
									const chartAlarmDateTime = xInPOR === true ? alarmDateTime : new Date(minDate);
									// console.log ( xInPOR, chartAlarmDateTime );
									const useNearestYValue = xInPOR === true ? true : false;
									const alarmObject = {
										'alarmDateTime': alarmDateTime.valueOf(),
										'chartAlarmDateTime': chartAlarmDateTime,
										'nearestYValue': nearestYValue,
										'useNearestYValue': useNearestYValue,
										'triggerValue': triggerValue,
										'dataType': dataType,
										'alarmState': alarm.alarmState,
										'alarmName': alarm.alarmName,
										'alarmMessage': this.createAlarmMessage(alarm) };
									alarmArray.push ( alarmObject );
									this.alarmList.push ( alarmObject );
								}
								// console.log ( annotationArray );
							}
						}
						currentChartsWithData++;
					}
				}
			}
			// console.log(alarmArray);

				// http://jsfiddle.net/VgCQV/
				// http://jsfiddle.net/gh/get/jquery/1.9.1/highslide-software/highcharts.com/tree/master/samples/highcharts/demo/synchronized-charts/
				// console.log(Highcharts.charts);

				if ( seriesArray.length > 0 ) {

					const HighChartChart = $('<div class="chart">')
						.appendTo('#StationDashboardHighChartsContainer')
						.highcharts({
							chart: {
								marginLeft: 120, // Keep all charts left aligned
								zoomType: 'x',
								spacingTop: 20,
								spacingBottom: 20,
								height: 200,
								events: {
									load: function() {
										//setTimeout(() => {
										self.drawAlarms(this, alarmArray);
										console.log ( "Chart loaded");
										//}, 2000);
									},
									render: function() {
										self.updateAlarmPositions(this);
										console.log("Chart rendered");
									}
								}
							},
							title: {
								text: '',
								align: 'left',
								margin: 0,
								x: 30
							},
							credits: {
								enabled: false
							},
							xAxis: {
								crosshair: true,
								events: {
									setExtremes: this.chartUtilitiesService.syncExtremes,
									// afterSetExtremes: this.chartUtilitiesService.updateTickMarks
								},
								plotLines: [{
									color: '#000000', // black
									width: 2,
									value: today.getTime(),
									label: {
										text: 'now',
										rotation: 0,
										textAlign: 'left'
									}
								}],
								labels: {
									enabled: showXAxisLabels,
									align: 'center',
									/*
									formatter: function() {
										var d = new Date(this.value);
													console.log(d);
													console.log(d.toLocaleDateString());
										return d.toLocaleDateString();
									}
									*/
									format: xAxisFormat
								},
								lineColor: lineColor,
								lineWidth: 3,
								offset: 10,
								softMin: minDate,
								softMax: maxDate,
								tickInterval: tickInterval,
								tickLength: tickLength,
								type: 'datetime'
							},
							yAxis: yAxis,
							tooltip: {
								borderWidth: 0,
								distance: 60,
								// split: true,
								headerFormat: '{point.x:%b %e %l:%M%p} ' + this.tzName + '<br>',  
								// see http://pubs.opengroup.org/onlinepubs/007908799/xsh/strftime.html
								style: {
									fontSize: '16px'
								},
								positioner: function() {
									const p = this.chart.hoverPoint;
									return ( self.tooltipPreference == "fixed") ?  {x: 130, y: 0} : { x: p.plotX, y: p.plotY-25};
								},
								shadow: false,
								backgroundColor: 'rgba(255,255,255,0.6)',
								/* 	This works but is supposedly a performance killer.
									See https://jsfiddle.net/BlackLabel/hom49t05/ for their recommendation
								*/
								formatter: function() {
									let tt  = '<b>' + Highcharts.dateFormat('%Y-%m-%e %l:%M%p', this.x) + '</b>';
									const actTime = this.x;
									this.series.chart.series.forEach(function(series, i) {
										// console.log(series); 
										if ( series.points.length > 0 ) {
										const diff = series.closestPointRange;
										let actPoint = series.points[series.points.length - 1];
										let color = series.color;
										series.data.forEach ( function(point, j) {
											if (point.x - actTime < diff ) {
												actPoint = point;
											}
										});
										if ( actPoint.y == null )
											actPoint = series.points[series.points.length - 2];

										let value = actPoint.y.toFixed(series.tooltipOptions.valueDecimals);
										if ( series.tooltipOptions.hasOwnProperty('tooltipReplace') && series.tooltipOptions.tooltipReplace != null ) {
											value = self.replaceValue ( value, series.tooltipOptions.tooltipReplace );
										}
										tt += '<br/>' + '<span style="color:' + color + '">' + value + '</span>';
										}
									});
									/*  different solution from elsewhere
									$(this.points[0].series.chart.series).each(function(i, series) {
										const diff = series.closestPointRange;
										let actPoint = null;
										$.each(series.data, function(i, point) {
											if (actTime - point.x < diff ) {
												actPoint = point;
											}
										});
										tt += '<br/>' + actPoint.y;
									});
									*/
									return tt;
								}
								// valueDecimals: dataset.valueDecimals
							},
							legend: {
								layout: 'horizontal',
								// margin: 30,
								align: 'right',
								// x: 60,
								verticalAlign: 'top',
								y: -25,
								floating: false
							},
							annotations: annotationArray,
							annotationsOptions: { enabledButtons: false },
							exporting: exportMenu,
							plotOptions: {
								series: {  // general options for all series
									turboThreshold: 100000,  // maximum number of data points, default is 1,000
									states: { inactive: { opacity: 1}}, // added this because the annotation line kept going light
									stickyTracking: false,
									gapSize: gapSize,
									gapUnit: 'value'
								}
							},
							series: seriesArray
						}
						/*
						, function(chart) {
							console.log("In this function", alarmArray);
							// add the alarm icons
							/*
							console.log(chart);
							chart.exportSVGElements[1].toFront();
							* /
							console.log(alarmArray);
							alarmArray.forEach ( alarm => {
								alarm['iconSize'] = 30;
								const x = chart.xAxis[0].toPixels(alarm.chartAlarmDateTime, false) - (alarm.iconSize / 2);
								const y = alarm.useNearestYValue === true ?
									chart.yAxis[0].toPixels(alarm.nearestYValue, false) - (alarm.iconSize / 2) :
									15;
								const align = alarm.useNearestYValue === true ? 'right' : 'left';
								const pngString = alarm.alarmState === 'Active' ?
									'assets/images/alarm-bell-large.png' :
									'assets/images/alarm-bell-large-actual-gray30x30.png';
								alarm['renderedImage'] = chart.renderer.image(
									pngString, x, y, alarm.iconSize, alarm.iconSize)
									.on('mouseover', function() {
										chart.customTooltip = chart.renderer.label(alarm.alarmMessage, x, y - 40 > 0 ? y - 40 : y + 40, true).attr({
											fill: 'rgba(0,0,0,0.5)',
											align: align,
											zIndex: 14
										}).css({
											fill: 'white'
										}).add();
									}).on('mouseout', function() {
										chart.customTooltip.destroy();
									})
									.add();
							});
							if ( alarmArray.length > 0 ) {
								chart['renderedAlarms'] = alarmArray;
							}
						}
								*/
					);
				}

			}
	}

	drawAlarms(chart: any, alarmArray: any[]) {
		//console.log("Drawing alarms", alarmArray);
							// add the alarm icons
							/* */
							/*
							console.log(chart);
							chart.exportSVGElements[1].toFront();
							*/
							alarmArray.forEach ( alarm => {
								alarm['iconSize'] = 30;
								const x = chart.xAxis[0].toPixels(alarm.chartAlarmDateTime, false) - (alarm.iconSize / 2);
								const y = alarm.useNearestYValue === true ?
									chart.yAxis[0].toPixels(alarm.nearestYValue, false) - (alarm.iconSize / 2) :
									15;
								const align = alarm.useNearestYValue === true ? 'right' : 'left';
								const pngString = alarm.alarmState === 'Active' ?
									'assets/images/alarm-bell-large.png' :
									'assets/images/alarm-bell-large-actual-gray30x30.png';
								alarm['renderedImage'] = chart.renderer.image(
									pngString, x, y, alarm.iconSize, alarm.iconSize)
									.on('mouseover', function() {
										chart.customTooltip = chart.renderer.label(alarm.alarmMessage, x, y - 40 > 0 ? y - 40 : y + 40, true).attr({
											fill: 'rgba(0,0,0,0.5)',
											align: align,
											zIndex: 14
										}).css({
											fill: 'white'
										}).add();
									}).on('mouseout', function() {
										chart.customTooltip.destroy();
									})
									.add();
							});
							if ( alarmArray.length > 0 ) {
								chart['renderedAlarms'] = alarmArray;
							}
	}

	updateAlarmPositions(chart: any) {
		console.log("Updating alarms");
		if ( chart.hasOwnProperty('renderedAlarms') && chart.renderedAlarms != null ) {
			chart.renderedAlarms.forEach(alarm => {
				const x = chart.xAxis[0].toPixels(alarm.chartAlarmDateTime, false) - alarm.iconSize / 2;
				const y = alarm.useNearestYValue ? chart.yAxis[0].toPixels(alarm.nearestYValue, false) - alarm.iconSize / 2 : 15;
				alarm.renderedImage.attr({ x, y });
			});
		}
	}

	// given a value, find it in the replacement array and return the replacement string
	replaceValue ( value, replacementArray ) {
		const replaceArray = replacementArray.filter (g => g['value'] == value);
		if ( replaceArray.length > 0 ) {
			return replaceArray[0].label;
		}
		return "";
	}

	// Calculate the number of days displayed in the charts
	numberOfDaysInChart () {
		if ( this.periodOfRecordSelected === '0' ) {	// custom dates
			return (this.endDate - this.startDate) / (24 * 60 * 60 * 1000);
		} else {
			return ( +this.periodOfRecordSelected);
		}
	}

	resetZoom () {
		Highcharts.charts.forEach( chart => {
			chart.xAxis[0].setExtremes(null, null);
		})	
	}

	// return true if the single alarm contains the given dataType, false otherwise
	doesAlarmMatchDataType(alarm, dataType) {
		if ( alarm.hasOwnProperty ('dataTypeNames')) {
			return alarm.dataTypeNames.includes(dataType);
		}
		return false;
	}

	isDateTimeWithinPOR ( dt, minDate, maxDate ) {
		const minDateT = (new Date (minDate)).getTime();
		const maxDateT = (new Date (maxDate)).getTime();
		const dtT = (new Date (dt)).getTime();
		return ( dtT >= minDateT && dtT <= maxDateT ) ? true : false;
	}

	getTodayAdjustment(ndays) {
		let adjustment = 0;
		const oneHour = 60*60*1000;
		const oneDay = 24*oneHour;
		if ( ndays <= .125 ) adjustment = oneHour/4.0; // 3 hours
		else if ( ndays <= .25 ) adjustment = oneHour/2.0; // 6 hours
		else if ( ndays <= .50) adjustment = oneHour; // 12 hours
		else if ( ndays <= 1.0) adjustment = 2*oneHour; // 1 day
		else if ( ndays <= 7.0) adjustment = oneDay; // 1 week
		else if ( ndays <= 30.0) adjustment = 2*oneDay; // 1 month
		else if ( ndays <= 90.0) adjustment = 6*oneDay; // 3 months
		else if ( ndays <= 180.0) adjustment = 10*oneDay; // 6 months
		else adjustment = 10*oneDay; // 1 year or more
		return adjustment;
	}

	// Given a date/time, run through the stationTSArray object, find the data for the given dateType
	// and return the nearest y value.
	// This is not written for maximum efficiency.  Replace with Highcharts function if available.
	// CAT: Now that ts is becoming an array, changed all references to ts to include [0] element.  Not sure I like this.  May need to revisit
	getNearestValueToDate (dataType, dateTime) {
		const targetTime = (new Date(dateTime)).getTime();
		let nearestValue = null;
		let smallestDifference = Number.MAX_SAFE_INTEGER;

		// console.log("Looking for datatype: " + dataType );
		this.stationTSArray.forEach ( chartObject => {
			// console.log(chartObject);
			// console.log("Comparing: " + chartObject.data.dataType + " and " + dataType );
			if ( chartObject.data.length > 0 && chartObject.data[0].dataType === dataType ) {
				const data = chartObject.data[0];
				// console.log("Found datatype: " + dataType );
				if ( data.ts.length > 0 ) {
					const ts = data.ts[0];
					for ( let i = 0; i < ts.data.length; i++ ) {
						// if the desired dateTime is the same as or past the current dateTime,
						// return that value.

						const currentTime = new Date(ts.data[i].dt).getTime()
						const difference = Math.abs(currentTime - targetTime);

						if ( difference < smallestDifference ) {
							smallestDifference = difference;
							nearestValue = ts.data[i].v;
						}

						if ( currentTime > targetTime && difference > smallestDifference ) {
							break;
						}
					}
				}
				/*
				// Hmmm.  Didn't find date/time.  Just return the last y value.
				if ( value === 0 ) {
					value = chartObject.data[0].ts[0].data[chartObject.data[0].ts[0].data.length - 1].v;
				}
					*/
			}
		});
		return nearestValue || 0;
	}

	// Create an element in the alarm object called dataTypeNames (stationSummary.alarms.dataTypeNames).
	// This will contain the data type names on which the alarms have occurred.
	assignAlarmAssociatedDataTypes ( stationSummary ) {
		for ( let i = 0; i < stationSummary.alarms.length; i++ ) {
			const pointId = stationSummary.alarms[i].pointId;
			const dataTypes = stationSummary.dataTypes.filter(dt => dt['associatedPointId'] === pointId);
			const dataTypeNames = [];
			for ( let j = 0; j < dataTypes.length; j++ ) {
				dataTypeNames.push ( dataTypes[j].name );
			}
			stationSummary.alarms[i]['dataTypeNames'] = dataTypeNames;
		}
	}

	// displayStationInformation displays information about the station in the
	displayStationInformation (  ) {

		const id = +this.stationID;  // create a numerical value of stationID
		const dataLayer = this.dataLayerService.getDataLayerDetails('allStations');
		const stations = dataLayer?.value?.stationSummaries;
		if ( stations == null ) {
			return;
		}

		const station_id = dataLayer.stationID;
		const novaScoresDictionary = dataLayer?.value?.novaScores;

		const station = stations.filter(dl => dl[station_id] === id)[0];
		// console.log(station);
		if ( station != null ) {
			this.currentStationSummary = station;
			const novaScores = this.chartUtilitiesService.getNovaScores(this.stationTSArray, station, novaScoresDictionary);
			this.stationNovaScores = novaScores;
			this.assignAlarmAssociatedDataTypes (this.currentStationSummary);
			this.stationAlarms = this.currentStationSummary.alarms;

			for ( let i = 0; i < novaScores.length; i++) {
				this.stationNovaScores[i]['novaScoreClass'] = 'novaScore' + novaScores[i].novaScoreGroup;
			}

			this.currentStation.stationName = station[dataLayer.stationName];
			this.currentStation.stationElevation = station.elevation;
		}
	}

	toggleTools() {
		this.showTools = !this.showTools;
	}

	tooltipPreferenceChanged(event: any) {
		this.appConfigService.setChartTooltipPreference( this.tooltipPreference );
	}


	//
	// clearGraphs removes the series from CombinedChart
	//
	clearGraphs() {
		for ( let chartIndex = 0; chartIndex < Highcharts.charts.length; chartIndex++ ) {
			// console.log( 'Number of Highchart charts: ' + Highcharts.charts.length);
			// console.log( Highcharts.charts);
			// console.log('Destroying a chart');
			let c = Highcharts.charts[chartIndex];
			// console.log(c);
			if ( c != undefined ) {
				c.destroy();
			}
		}
		Highcharts.charts.length = 0;

		const div = document.getElementById('StationDashboardHighChartsContainer');
		if ( div == null ) {
			return;
		}

		// remove all existing charts
		while (div.hasChildNodes()) {
			div.removeChild(div.lastChild);
		}
	}


	newStationSelected (newStation) {
		/*
		const tree = this._router.parseUrl(this.location.path());
		const link = ['/stationDashboard/' + newStation];
		this._router.navigate(link, { queryParams: tree.queryParams});
		// this.location.replaceState('/stationDashboard/' + newStation, tree.queryParams.toString());
		*/
		const link = '/stationDashboard/' + newStation;
		this.appConfigService.goToRouterLink(link);

		this.stationID = newStation;
		this.listSelection = +this.stationID;
		this.currentTab = "data";
		this.clearGraphs();
		this.displayGraphsForStationID();
	}

	newPeriodOfRecordSelected (newPOR) {
		// verify the dates are valid if a custom date range has been requested
		if ( newPOR === '0' && this.startDate > this.endDate) {
			alert('Start date is after end date.  Please select a different start or end date.')
			return;
		}
		this.periodOfRecordSelected = newPOR;
		this.clearGraphs();
		this.displayGraphsForStationID();
	}

	// given an array of novaScoreobjects from getNovaScores, return the highest
	getMaxNovaScore ( novaScores ) {
		let maxNovaScore = 1;
		for ( let i = 0; i < novaScores.length; i++) {
			maxNovaScore = Math.max(maxNovaScore, novaScores[i].novaScoreGroup);
		}
		return maxNovaScore;
	}

	// given a novaScore group number, return the corresponding color
	// these match the color use on the map
	getNovaScoreClass(maxNovaScore) {
		let novaScoreClass = '';
		switch (maxNovaScore) {
			case 5: novaScoreClass = 'novaScore5'; break;
			case 4: novaScoreClass = 'novaScore4'; break;
			case 3: novaScoreClass = 'novaScore3'; break;
			case 2: novaScoreClass = 'novaScore2'; break;
			default: novaScoreClass = 'novaScore1'; break;
		}
		return novaScoreClass;
	}

	// this populates the station selection pulldown
	populateStationSelection() {

		// need to call parseAllStations directly so we know all stations have been read before continuing
		this.dataLayerService.parseAllStations().then(allStations => {
			// console.log('CAT:', allStations);
			this.stationList = [];
			const stations = allStations['stationSummaries'];

			const dataLayer = this.dataLayerService.getDataLayerDetails('allStations');
			// console.log(dataLayer);
			// CAT - when the smoke clears, read this https://medium.com/@fsufitch/angular-2-objectunsubscribederror-woes-abda60e04cfe
			/*
			// uncomment this section if you want auto updates to occur on the dashboard as well
			this.subscriptions.push ( dataLayer.valueObservable.subscribe ( data => {
				console.log('Refreshing table ');
				// console.log(data);
				this.clearGraphs();
				this.displayGraphsForStationID();
			}));
			*/

			const station_id = dataLayer.stationID;
			const station_name = dataLayer.stationName;
			const novaScoresDictionary = dataLayer.value.novaScores;
			this.alarmTriggers = dataLayer.value.alarmTriggers;

			// tslint:disable-next-line:forin
			for ( const i in stations ) {
				let newStation: any; // StationData;
				// const novaScores = this.getNovaScores(stations[i], novaScoresDictionary);
				const novaScores = this.chartUtilitiesService.getNovaScores(this.stationTSArray, stations[i], novaScoresDictionary);
				// console.log(novaScores);
				const maxNovaScore = this.getMaxNovaScore(novaScores);
				const novaScoreClass = this.getNovaScoreClass(maxNovaScore);
				// console.log( stations[i][station_name] + ": " + maxNovaScore + ": " + novaScoreClass );
				newStation = {
						id: stations[i][station_id],
						name: stations[i][station_name],
						label: stations[i][station_id] + ' - ' + stations[i][station_name] + ' (' + maxNovaScore + ')',
						value: stations[i][station_id],
						novaScore: maxNovaScore,
						novaScoreClass:  novaScoreClass};

				this.binaryInsertByNovaScore( newStation, this.stationList, undefined, undefined );
			}

			console.log('Number of stations before/after sort ' + stations.length + '/' + this.stationList.length );
			// if the user arrived here by clicking on "Station Dashboard" rather than by selecting an individual station,
			// a reasonable default must be selected.
			if ( this.stationID === -999 && this.stationList.length > 0) {
				this.setDefaultStation();
			} else {
				this.listSelection = +this.stationID;
			}
		});
	}

	// setDefaultStation sets the dashboard to display the first station in the station selection
	// list if no previous stations have been viewed.
	setDefaultStation() {
		this.stationID = this.stationList[0].id;
		this.newStationSelected(this.stationID);
	}

	// utility function for populateStationSelection
	// orders list by id only
	binaryInsert(newStation, array, startVal, endVal) {
		const length = array.length;
		const start = typeof(startVal) !== 'undefined' ? startVal : 0;
		const end = typeof(endVal) !== 'undefined' ? endVal : length - 1; // !! endVal could be 0 don't use || syntax
		const m = start + Math.floor((end - start) / 2);

		if (length === 0) {
			array.push(newStation);
			return;
		}

		if (newStation.id > array[end].id) {
			array.splice(end + 1, 0, newStation);
			return;
		}

		if (newStation.id < array[start].id) {
			array.splice(start, 0, newStation);
			return;
		}

		if (start >= end) {
			return;
		}

		if (newStation.id < array[m].id) {
			this.binaryInsert(newStation, array, start, m - 1);
			return;
		}

		if (newStation.id > array[m].id) {
			this.binaryInsert(newStation, array, m + 1, end);
			return;
		}

		// don't insert duplicates
	}

	// utility function for populateStationSelection
	// orders list by id and novaScore
	binaryInsertByNovaScore(newStation, array, startVal, endVal) {
		const length = array.length;
		const start = typeof(startVal) !== 'undefined' ? startVal : 0;
		const end = typeof(endVal) !== 'undefined' ? endVal : length - 1; // !! endVal could be 0 don't use || syntax
		const m = start + Math.floor((end - start) / 2);

		if (length === 0) {
			array.push(newStation);
			return;
		}

		if (newStation.novaScore < array[end].novaScore || (newStation.novaScore === array[end].novaScore && newStation.id > array[end].id)) {
			array.splice(end + 1, 0, newStation);
			return;
		}

		if (newStation.novaScore > array[start].novaScore ||
			(newStation.novaScore === array[start].novaScore && newStation.id < array[start].id)) {
			array.splice(start, 0, newStation);
			return;
		}

		if (start >= end) {
			return;
		}

		if (newStation.novaScore > array[m].novaScore || (newStation.novaScore === array[m].novaScore && newStation.id < array[m].id)) {
			this.binaryInsertByNovaScore(newStation, array, start, m - 1);
			return;
		} else {
			this.binaryInsertByNovaScore(newStation, array, m + 1, end);
			return;
		}

		// don't insert duplicates
	}

	// If the station images are in a static file, get the contents of that file.
	parseImageFile(): Observable<any> {
		const baseDir = this.configService.baseDir;
		const fullPath = baseDir + 'system/stationImages.json';
		console.log('Reading image definition file', fullPath);
		if ( fullPath !== '') {
			return this.http.get(fullPath);
		} else {
			return of('{}');
		}
	}

	// Retrieve the definition of images associated with each station.
	readAllImages() {
		// console.log('In readAllImages');
		this.parseImageFile()
			.subscribe ( imageObject => {
				// console.log('Read image file', imageObject);
				if ( imageObject.hasOwnProperty('stationImages')) {
					// console.log('Setting allImages to ', imageObject.stationImages);
					this.allImages = imageObject.stationImages;
				}
				// this.setImagesProcessedTrue();
			});
	}

	// If the station elevations are in a static file, get the contents of that file.
	parseElevationProfileFile(): Observable<any> {
		const baseDir = this.configService.baseDir;
		const fullPath = baseDir + 'system/elevationProfiles.json';
		console.log('Reading elevation profile definition file', fullPath);
		if ( fullPath !== '') {
			return this.http.get(fullPath).pipe(
				catchError(error => {
					console.error ("Can't read system/elevationProfiles.json");
					return of('{}');
				})
			);
		} else {
			return of('{}');
		}
	}

	// Retrieve the definition of images associated with each station.
	readAllElevationProfiles() {
		// console.log('In readAllImages');
		this.parseElevationProfileFile()
			.subscribe ( elevationProfileDefs => {
				// console.log('Read image file', imageObject);
				if ( elevationProfileDefs.hasOwnProperty('elevationProfiles')) {
					// console.log('Setting allImages to ', imageObject.stationImages);
					this.allElevationProfiles = elevationProfileDefs.elevationProfiles;
					console.log(this.allElevationProfiles);
				}
				// this.setImagesProcessedTrue();
			}, error => {
				console.log("No elevation profile configuration file found, system/elevationProfiles.json.")
			});
	}

	// process the entire list of images and display those whose id matches the current station id
	displayImages() {
		console.log('In displayImages', this.allImages, this.stationID );
		this.images = [];
		// if ( this.allImages === [] ||
		if ( this.allImages.length == 0 || this.stationID === '') {
			console.log('Something amiss in displayImages');
			return;
		}
		const id = '' + this.stationID;
		console.log(this.currentStation);

		const imagesDefinition = this.allImages.filter(function(item) {
			console.log('Comparing', item.stationId, id);
			return item.stationId === id;
		})[0];
		if ( imagesDefinition === undefined || imagesDefinition.length === 0 ) {
			return;
		}
		console.log ( imagesDefinition );
		imagesDefinition.images.forEach(element => {
			this.images.push({ source: this.configService.baseDir + element.source,
				thumbnail: this.configService.baseDir + element.thumbnail, title: element.title,
				link: element?.link, linkLabel: element?.linkLabel });
		}, this);
	}

	// process the entire list of elevation profiles and display the one whose id matches the current station id
	displayElevationProfile() {
		// console.log('In displayElevationProfile', this.allElevationProfiles, this.currentStation.stationId );
		this.elevationProfile = null;
		if ( this.allElevationProfiles.length == 0 || this.stationID === '') {
			console.log('Something amiss in displayElevationProfile');
			return;
		}
		const id = '' + this.stationID;
		console.log(this.currentStation);

		const elevationProfile = this.allElevationProfiles.filter(function(item) {
			//console.log('Comparing', item.stationId, id);
			return item.stationId === id;
		})[0];
		if ( elevationProfile === undefined ) {
			return;
		}
		console.log ( elevationProfile );

		this.http.get(this.configService.baseDir + elevationProfile.source + '?nocache=' + (new Date()).getTime())
			.subscribe ({ 
				next: epDef => {
					// console.log(epDef);
					if ( epDef == null ) {
						this.elevationProfile = epDef;
						console.log("No elevation profile");
						return;
					}
					const observableCommandArray: any[] = [];
					// sort through all tsServiceParameters, get data for all tsServiceParameters,
					// fill in "Value", then set to this.elevationProfile	
					if (epDef.hasOwnProperty('tsproducts')) {
						const tsproducts = epDef['tsproducts'];
						
						// do this loop twice - once to create the request and then after the fork Join to get the value
						for ( let i=0; i < tsproducts.length; i++) {
							const tsproduct = tsproducts[i];
							const subProducts = tsproduct?.subProducts;
							if ( subProducts != undefined) {
								// console.log(subProducts);
								for ( let j=0; j < subProducts.length; j++ ) {
									const subproduct = subProducts[j];
									// console.log(subproduct);
									const datas = subproduct['data'];
									if ( datas != undefined ) {
										for ( let k=0; k < datas.length; k++ ) {
											const data = datas[k];
											if ( data.hasOwnProperty('properties')) {
												if ( data.properties.hasOwnProperty('tsServiceParameters')) {
													// FINALLY - we have a statistic to retrieve
													const tsServiceParameters = data.properties.tsServiceParameters;
													console.log("Getting: ", tsServiceParameters);
													observableCommandArray.push(this.dataWebServices.getService(tsServiceParameters)
														.pipe ( catchError ( error => of(null))));
												}
											}
										}
									}
								}
							}
						}
						const observableArray = forkJoin(observableCommandArray);
						observableArray.subscribe({
							next: epAnswer => {
								console.log(epAnswer);
								let answerIndex = 0;
								for ( let i=0; i < tsproducts.length; i++) {
									const tsproduct = tsproducts[i];
									const Y2 = tsproduct?.properties?.DataY2;
									const subProducts = tsproduct?.subProducts;
									if ( subProducts != undefined) {
										// console.log(subProducts);
										for ( let j=0; j < subProducts.length; j++ ) {
											const subproduct = subProducts[j];
											// console.log(subproduct);
											const datas = subproduct['data'];
											if ( datas != undefined ) {
												for ( let k=0; k < datas.length; k++ ) {
													const data = datas[k];
													if ( data.hasOwnProperty('properties')) {
														if ( data.properties.hasOwnProperty('tsServiceParameters')) {
															// FINALLY - we have a statistic to fill
															const response: any = epAnswer[answerIndex];
															// console.log("response:", response );
															const v = response['stationStatistics'][0]?.statistics[0]?.statisticValue;
															console.log("Elevation profile, assigning value:", v );
															data.properties.Value = v;
															data.properties['LinkHelp'] = v;

															// add another property - line across
															// use FillColor to find corresponding annotation
															const FillColor = data.properties.FillColor;
															const annotations = subproduct['annotations'];
															for ( let l=0; l<annotations.length; l++ ) {
																let annotation = annotations[l];
																if ( annotation.properties.Type == "line" && annotation.properties.LineColor == FillColor) {
																	const v2 = Y2 - data.properties.YOffset - data.properties.Height + v;
																	annotation.properties.Y1 = v2;
																	annotation.properties.Y2 = v2;
																	console.log("Setting value line", v2);
																}
															}

															answerIndex++;
														}
													}
												}
											}
										}
									}
								}
								// console.log(epDef);
								this.elevationProfile = epDef;
							}
						})
					}

				}
			});

	}

	// set the forecastPlotConfiguration, if any.  Otherwise, sets to null so tab isn't displayed.
	displayForecastPlot(stationTypeId: number) {
		const forecastStationId = this.configService.getForecastStationId();
		this.forecastPlotConfiguration = null;
		const self = this;

		/*
		* Step 1 - determine if this is a forecst station, return if not
		*/
		if ( stationTypeId != forecastStationId ) {
			// this is not a forecast station
			console.log(stationTypeId, forecastStationId);
			return;
		}

		//this.getRatingTable();

		/* 
		* Step 2 - get the data associated with WaterLevelRiver
		*/
		const waterLevelRiverArray = this.stationTSArray.filter((ts) => {
			if ( ts.data.length > 1 ) {
				console.log("More than 1 data type defined for this chart.  Using the first one, but this should be improved.") 
			}
			return (ts?.data[0].dataType == "WaterLevelRiver");
		});

		const waterLevelRiver = waterLevelRiverArray.length > 0 ? waterLevelRiverArray[0] : null;
		if ( waterLevelRiver == null ) {
			console.log("Can't find WaterLevelRiver. Unable to create forecast plot.");
			return;
		}

		/*
		* Step 3 - get the WaterLevelTime Series time series 
		*/
		const locId = this.stationID + "-" + this.stationID;
		console.log(waterLevelRiver);
		let waterLevelRiverTSArray: any[] = [];

		if ( waterLevelRiver.hasOwnProperty('data') && waterLevelRiver.data.length > 0 ) {
			waterLevelRiverTSArray = waterLevelRiver?.data[0].ts.filter((wl) => {
				return wl.locId == locId;
			});
		} else { 
			console.log("No WaterLevelRiver data.  Unable to create forecast plot.")
			return;
		}

		if ( waterLevelRiverTSArray.length == 0 ) {
			console.log("No WaterLevelRiver data.  Unable to create forecast plot.")
			return;
		}

		// get and format the data
		const waterLevelRiverTS = waterLevelRiverTSArray[0];
		const theWaterRiverData: any[] = [];
		this.chartUtilitiesService.convertTSDataToHighchartsFormat(theWaterRiverData, waterLevelRiverTS, "", false, []);
		const waterLevelRiverTSName = waterLevelRiverTS?.locId + " (" + waterLevelRiverTS?.description + ")";

		// convert from stage to discharge and add to point
		for ( let j=0; j<theWaterRiverData.length; j++) {
			const stageValue = theWaterRiverData[j].y;
			const flowEquivalent = this.getFlowFromStage(stageValue);
			theWaterRiverData[j]['flowValue'] = flowEquivalent;
		}

		const chartStartDate = theWaterRiverData[0]['x'];

		const tmpForecastPlotConfiguration = this.chartUtilitiesService.getDefaultTSConfiguration();

		//height
		tmpForecastPlotConfiguration['chart']['height'] = 600;

		// series
		tmpForecastPlotConfiguration['series'] = [{ "data": theWaterRiverData, "name": waterLevelRiverTSName }];

		/*
		* Step 4 - get data associated with the forecasted flow 
		*/
		const dischargeRiverArray = this.stationTSArray.filter((ts) => {
			if ( ts.data.length > 1 ) {
				console.log("More than 1 data type defined for this chart.  Using the first one, but this should be improved.") 
			}
			return (ts?.data[0].dataType == "DischargeRiver");
		});
		console.log(dischargeRiverArray);

		const dischargeRiver = dischargeRiverArray.length > 0 ? dischargeRiverArray[0] : null;
		if ( dischargeRiver == null ) {
			console.log("Can't find DischargeRiver. Unable to create forecast plot.");
			return;
		}

		/*
		* Step 5 - get the forecasted flow time series (plural)
		*/
		let dischargeRiverTSArray: any[] = [];

		if ( dischargeRiver.hasOwnProperty('data') && dischargeRiver.data.length > 0 ) {
			dischargeRiverTSArray = dischargeRiver?.data[0].ts;
		} else { 
			console.log("No DischargeRiver data.  Unable to create forecast plot.")
			return;
		}

		if ( dischargeRiverTSArray.length == 0 ) {
			console.log("No DischargeRiver data.  Unable to create forecast plot.")
			return;
		}

		// get and format the data
		let forecastStartDate = null;
		let forecastEndDate = null;
		for ( let i = 0; i < dischargeRiverTSArray.length; i++) {
			const dischargeRiverTS = dischargeRiverTSArray[i];
			const theDischargeData: any[] = [];
			// skip anything that has a locId of xxx-3xxx
			const locId = dischargeRiverTS?.locId;
			const dashLoc = dischargeRiverTS?.locId.indexOf("-");
			if ( dashLoc != -1 && dashLoc != locId.length-1 ) {
				if ( locId[dashLoc+1] == '3') {
					continue;
				}
			}

			this.chartUtilitiesService.convertTSDataToHighchartsFormat(theDischargeData, dischargeRiverTS, "", false, []);
			const dischargeRiverTSName = locId + " (" + dischargeRiverTS?.description + ")";
				forecastStartDate = theDischargeData[0]['x'];
				forecastEndDate = theDischargeData[theDischargeData.length-1]['x'];

			// convert from discharge to stage

			for ( let j=0; j<theDischargeData.length; j++) {
				const flowValue = theDischargeData[j].y;
				theDischargeData[j]['flowValue'] = flowValue;
				const stageEquivalent = this.getStageFromFlow(flowValue);
				theDischargeData[j].y = stageEquivalent;
			}

			tmpForecastPlotConfiguration['series'].push ({ "data": theDischargeData, yAxis: 0, "name": dischargeRiverTSName });
		}

		console.log(chartStartDate, forecastStartDate, forecastEndDate);

		/*
		* Step 6 - configure remaining chart options
		*/

		// configure export menu
		const exportMenu = this.chartUtilitiesService.getExportMenuDefinition(true);
		tmpForecastPlotConfiguration['exporting'] =  exportMenu;

		// xaxis
		const numberOfDays = this.numberOfDaysInChart ();
		const xAxisFormat = this.chartUtilitiesService.formulateXAxisFormat(numberOfDays);
		const tickInterval = this.chartUtilitiesService.calculateTickInterval (numberOfDays);
		tmpForecastPlotConfiguration['xAxis']['labels']['format'] = xAxisFormat;
		tmpForecastPlotConfiguration['xAxis']['tickInterval'] = tickInterval;
		tmpForecastPlotConfiguration['xAxis']['plotBands'] = [
			{
				label: { 
					text: "OBSERVED",
					y: -5
				},
				from: chartStartDate,
				// from: theWaterRiverData[0]['x'],
				to: forecastStartDate,
				color: '#f0f0f0',
				zIndex: 0
			},
			{
				label: { 
					text: "FORECAST",
					y: -5
				},
				from: forecastStartDate,
				to: forecastEndDate,
				// to: theWaterRiverData[theWaterRiverData.length-1]['x'],
				color: '#ffffff'
			},
		];


		// yaxis
		tmpForecastPlotConfiguration['yAxis'] = [{ // left
			id: 'stage-axis',
			title: {
				text: "Stage (ft)"
			},
			labels: {
				format: '{value:.1f}'
			},
			lineWidth: 1
		}, { // right
			id: 'flow-axis',
			// type: 'logarithmic',
			title: {
				text: "Flow (cfs)"
			},
			lineWidth: 1,
			/* start temporary */
			startOnTick: false,
			endOnTick: false,
			// Force same tick positions as left axis
			linkedTo: 0,
			labels: {
				formatter: function() {
					const flow = self.getFlowFromStage(this.value);
					return Highcharts.numberFormat(flow, 0); 
				} 
			}, 
			opposite: true
		}];

		// again - really hard-coding here
		tmpForecastPlotConfiguration['yAxis'][0]['plotBands'] = [
			{
				label: { text: "Action: 115.5"},
				from: 115.5,
				to: 117.5,
				color: '#ffffe6',
				borderColor: 'black',
				borderWidth: 1,
				zIndex: 1
			},
			{
				label: { text: "Minor: 117.5"},
				from: 117.5,
				to: 119.5,
				color: '#fff5e3',
				borderColor: 'black',
				borderWidth: 1,
				zIndex: 1
			},
			{
				label: { text: "Moderate: 119.5"},
				from: 119.5,
				to: 122.5,
				color: '#ffe5e6',
				borderColor: 'black',
				borderWidth: 1,
				zIndex: 1
			},
			{
				label: { text: "Major: 122.5"},
				from: 122.5,
				to: 126.0,
				color: '#fde9ff',
				borderColor: 'black',
				borderWidth: 1,
				zIndex: 1
			}
		];

		// tooltip
		/*
		tmpForecastPlotConfiguration['tooltip']['headerFormat'] = '{point.x:%Y-%m-%e %l:%M%p} '+ this.tzName + '<br>';  
		tmpForecastPlotConfiguration['positioner'] = function() {
			const p = this.chart.hoverPoint;
			return ( this.tooltipPreference == "fixed") ?  {x: 130, y: 0} : { x: p.plotX, y: p.plotY-25};
		};
		*/

		tmpForecastPlotConfiguration['tooltip']['useHTML'] = true;
		tmpForecastPlotConfiguration['tooltip']['formatter'] = function() {
			let tt  = '<b>' + Highcharts.dateFormat('%Y-%m-%e %l:%M %p', this.x) + '</b>';
			tt += '<br/><span style="color:' + this.series.color + '">\u25CF</span> ' + this.series.name
			tt += '<br/>Stage: ' + this.y.toFixed(1) + " ft";
			tt += '<br/>Flow: ' + this.point.flowValue.toFixed(0) + " cfs";
			return tt;
		}

		// legend
		tmpForecastPlotConfiguration['legend'] = {
			layout: 'horizontal',
			// margin: 30,
			align: 'center',
			// x: 60,
			verticalAlign: 'top',
			y: -5,
			floating: false
		};

		// title 
		tmpForecastPlotConfiguration['title'] = {
			text: this.currentStation.stationName
		}

		// subtitle
		/*
		tmpForecastPlotConfiguration['subtitle'] = {
			text: "OBSERVED",
			align: 'left',
			x: +250,
			y: +170
		}
		*/


		// "now" line
		const today = new Date();
		tmpForecastPlotConfiguration['xAxis']['plotLines'] = [
			{
							color: '#000000', // black
							zIndex: 10,
							width: 2,
							value: today.getTime(),
							label: {
								text: 'now',
								rotation: 0,
								textAlign: 'left',
								y: 30 // y value of text, positive is from top working down
							}
			},
			{
							color: 'blue', // black
							zIndex: 10,
							width: 2,
							dashStyle: 'dash',
							value: forecastStartDate,
							label: {
								rotation: 0,
								textAlign: 'left',
								y: 30
							}
			}
		];

		this.forecastPlotConfiguration = tmpForecastPlotConfiguration;
		console.log(this.forecastPlotConfiguration);

		setTimeout(() => {
			const theDiv = document.getElementById('forecastPlotDiv');
			// console.log(theDiv);
			const forecastPlot = Highcharts.chart('forecastPlotDiv', this.forecastPlotConfiguration);
			/* start temporary */
			// old way when flow was associated with right y axis
			let doit = false;
			forecastPlot.yAxis[0].setExtremes(108, 124, true);

			if ( doit ) {
			// get lowest and highest flow tick values
			//const flowTickPositions = (forecastPlot?.yAxis[1].tickPositions);
			//if ( flowTickPositions != undefined ) {
				/*
				const flowTickPositionLow = flowTickPositions[0];
				// make high one past the current
				const lastFlowTickIndex = flowTickPositions.length - 1;
				const flowTickInterval = flowTickPositions[lastFlowTickIndex] - flowTickPositions[lastFlowTickIndex-1]
				// now we are really hard-coding - change this strategy in production
				// we will actually need to retrieve the alarm levels and consider that when calculating the high stage extreme
				// max between stageTickPositionHigh and alarm level (alarm level will probably trump)
				const flowTickPositionHigh = flowTickPositions[lastFlowTickIndex] + (3*flowTickInterval);
				// convert to stage values
				const stageTickPositionLow = Math.min(this.getStageFromFlow(flowTickPositionLow), 108);
				const stageTickPositionHigh = Math.max(this.getStageFromFlow(flowTickPositionHigh), 124);
				console.log(stageTickPositionLow, stageTickPositionHigh);
				forecastPlot.yAxis[0].setExtremes(stageTickPositionLow, stageTickPositionHigh);
				*/
				// pause, then create tick marks for flow based on new stage ticks marks
				setTimeout(() => {
					const stageTickPositions = forecastPlot.yAxis[0].tickPositions;
					if ( stageTickPositions == undefined ) {
						return;
					}
					console.log(stageTickPositions);
					const newFlowTickPositions: any[] = [];
					for ( let i=0; i < stageTickPositions.length; i++) {
						// console.log("converting stage value to flow: " + stageTickPositions[i]);
						const newFlow = this.getFlowFromStage(stageTickPositions[i]);
						const newFlowFixed = +newFlow.toFixed(0);
						newFlowTickPositions.push ( ""+ newFlowFixed);
					}
					console.log(newFlowTickPositions);
					//forecastPlot.yAxis[1].setExtremes(newFlowTickPositions[0], newFlowTickPositions[newFlowTickPositions.length-1], true);
					forecastPlot.yAxis[1].update({
						categories: newFlowTickPositions,
						min: 0, max: 9
						//tickPositions: newFlowTickPositions
					}, true); // true == redraw
					//forecastPlot.yAxis[1].setExtremes(newFlowTickPositions[0], newFlowTickPositions[newFlowTickPositions.length-1]);

				}, 2000);
			//}
			}
			//const lastStageValue = theWaterRiverData[theWaterRiverData.length-1].y;
			//const correspondingFlowValue = 
			//forecastPlot.yAxis[0].setExtremes(109, 117);
			/* end temporary */
		}, 1000);
	}

	/** start all temporary code */
  	interpolate ( x: number, xmin: number, xmax: number, ymin: number, ymax: number ): number {
    	if ( (xmax - xmin) == 0.0 ) {
      		return ymin;
    	}
    	else {
        	return ( ymin + (ymax - ymin)*(x - xmin)/(xmax - xmin) );
    	}
  	}
	// stageOrFlow is 0 for stage, 1 for flow
	// value is the value to find the index for
	// resulting index is the index of the first item in stageFlowArray that EXCEEDS the value if the exact value isn't in the list
	// OR the index matching the exact value
	// OR -1 if no value exceeds value
	findStageFlowIndex( stageOrFlow: number, value: number) {
		let index = this.stageFlowArray.findIndex(n => n[stageOrFlow] >= value);
		return index;
	}

	getFlowFromStage (stageValue: number): number {
		const index = this.findStageFlowIndex(0, stageValue);
		if ( index == -1 ) {
			return ( this.stageFlowArray[this.stageFlowArray.length-1][1]);
		} else if ( index == 0 ) {
			return ( this.stageFlowArray[index][1]);
		} else {
			const interp = this.interpolate ( 
				stageValue,
				this.stageFlowArray[index-1][0],
				this.stageFlowArray[index][0],
				this.stageFlowArray[index-1][1],
				this.stageFlowArray[index][1]
			);
			return ( interp );
		}
	}

	getStageFromFlow( flowValue: number): number {
		const index = this.findStageFlowIndex(1, flowValue);
		if ( index == -1 ) {
			return ( this.stageFlowArray[this.stageFlowArray.length-1][0]);
		} else if ( index == 0 ) {
			return ( this.stageFlowArray[index][0]);
		} 
		else {
			const interp = this.interpolate ( 
				flowValue,
				this.stageFlowArray[index-1][1],
				this.stageFlowArray[index][1],
				this.stageFlowArray[index-1][0],
				this.stageFlowArray[index][0]
			);
			return (interp);
		}
	}

	//started this routine and then talked to Steve who said DWS would do this for me
	getRatingTable() {
		// step 1 - get points and find the one whose point_numid = numid (station)
		//this.dataWebServices.getService("points?pointNumId=" + this.stationID).subscribe ({
			//next: point => {

			// assume point_numid is same as station numid
				this.dataWebServices.getService("ratingAssigns?pointNumId=" + this.stationID).subscribe ({
					next: ratingAssigns => {
						// get most recent rating assign
						console.log(ratingAssigns);
						// create a new list with unique "number" and most recent date for each
						const ralist: any[] = [];

						// get unique list of "number"
						const numbers: any[] = [];
						ratingAssigns.forEach((ra:any) => {
							numbers.push(ra['number']);
						});
						const uniqueNumbers = [...new Set(numbers)];

						// filter currentpointRatingAssignList for just that number
						uniqueNumbers.forEach((num: number) => {
							const uRA = ratingAssigns.filter((ra: any) => {
							return ra.number == num
							});
							// now get most recent for that 'number'  
							const singleRA = uRA.reduce((a:any,b:any) => (a.effective_time > b.effective_time ? a : b));
							ralist.push(singleRA);
						});
						// reduce ralist to the "one"
						// now get rating table
					}
				})
			//}
		//})
	}

	// create array of arrays [stage,flow]
	stageFlowArray = [
		[108.5, 0],
		[109, 3.05],
		[109.1, 3.85],
		[109.2, 6.4],
		[109.3, 11],
		[109.4, 18],
		[109.5, 27],
		[109.56, 38],
		[109.66, 51],
		[109.76, 64],
		[109.86, 79],
		[110, 95],
		[110.11, 111],
		[110.2, 128],
		[110.3, 145],
		[110.36, 164],
		[110.5, 183],
		[110.6, 204],
		[111, 293],
		[111.1, 317],
		[111.2, 341],
		[111.3, 367],
		[111.5, 419],
		[111.6, 447],
		[111.7, 475],
		[111.8, 504],
		[111.86, 533],
		[112, 563],
		[112.1, 594],
		[112.2, 626],
		[112.3, 658],
		[112.4, 691],
		[112.5, 724],
		[112.6, 758],
		[112.7, 793],
		[112.8, 828],
		[112.9, 864],
		[113, 901],
		[113.1, 938],
		[113.2, 976],
		[113.3, 1014],
		[113.4, 1053],
		[113.5, 1092],
		[113.6, 1133],
		[113.7, 1173],
		[113.8, 1215],
		[113.86, 1257],
		[114, 1299],
		[114.1, 1342],
		[114.2, 1386],
		[114.3, 1430],
		[114.4, 1474],
		[114.5, 1520],
		[114.6, 1566],
		[114.7, 1612],
		[114.8, 1659],
		[114.9, 1706],
		[115, 1754],
		[115.1, 1803],
		[115.2, 1852],
		[115.4, 1952],
		[115.5, 2002],
		[115.6, 2053],
		[115.7, 2105],
		[115.8, 2157],
		[115.86, 2210],
		[116, 2279],
		[116.5, 2551],
		[117, 2836],
		[117.5, 3133],
		[118, 3439],
		[118.5, 3749],
		[119, 4069],
		[119.5, 4399],
		[120, 4739],
		[120.5, 5085],
		[121, 5440],
		[121.5, 5805],
		[122, 6177],
		[122.5, 6557],
		[123, 6945],
		[123.5, 7341],
		[124, 7746],
		[124.4, 8050],
		[127.3, 9892]
	];
	/** end all temporary code */
}
